// Les 10 du jour (spec § 11) : fiches à traiter aujourd'hui, choisies chaque nuit par `radar priorities`, chacune
// avec les raisons qui la font sortir et la source de chaque raison.
import { projectName } from "./projectText";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocaleState, useNotify, useRedirect, useTranslate } from "ra-core";
import { ExternalLink, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { useConfigurationContext } from "../root/ConfigurationContext";
import {
  type DailyPick,
  type DailyPicksToday,
  type PickReason,
  parseBudget,
  reasonMessage,
} from "./dailyPickReasons";
import { DailyTeasers } from "./DailyTeasers";
import { PhaseBadge } from "./PhaseBadge";
import { countryName, formatBudget, formatDay, phaseKey } from "./projectUtils";
import { useScreenTip, type TipSpec } from "../onboarding/useScreenTip";
import { useInvited } from "./useInvited";
import { HelpTip } from "./HelpTip";

const TIPS: TipSpec[] = [{ target: "reasons", key: "daily.reasons" }];

const QUERY_KEY = ["daily_picks_today"];

export const DailyPicks = () => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const t = (key: string, options?: object) =>
    translate(`resources.projects.daily_picks.${key}`, options);
  const { data, isPending } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const { data, error } =
        await getSupabaseClient().rpc("daily_picks_today");
      if (error) throw error;
      return data as DailyPicksToday;
    },
  });
  const today = new Date().toISOString().slice(0, 10);
  const stale = !!data?.day && data.day < today;
  const invited = useInvited();
  useScreenTip("tip:daily", TIPS, (data?.picks.length ?? 0) > 0);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="flex items-center gap-1 text-sm font-bold">
          {t("title")}
          <HelpTip topic="daily" />
        </h3>
        {data?.day && (
          <span className="text-xs text-muted-foreground">
            {t(stale ? "computed_on" : "computed_today", {
              date: formatDay(data.day, locale),
            })}
          </span>
        )}
      </div>
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {isPending ? (
          <div className="h-24" />
        ) : !data?.day ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            {t("not_computed")}
          </p>
        ) : !data.picks.length ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            {t("empty")}
          </p>
        ) : (
          <ol className="divide-y">
            {data.picks.map((pick, index) => (
              <PickRow key={pick.id} pick={pick} first={index === 0} />
            ))}
          </ol>
        )}
        {invited && <DailyTeasers />}
      </div>
    </section>
  );
};

const PickRow = ({ pick, first }: { pick: DailyPick; first: boolean }) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const t = (key: string, options?: object) =>
    translate(`resources.projects.daily_picks.${key}`, options);
  const notify = useNotify();
  const redirect = useRedirect();
  const queryClient = useQueryClient();
  const invited = useInvited();
  const { dealStages } = useConfigurationContext();
  const [busy, setBusy] = useState(false);

  const dismiss = async () => {
    setBusy(true);
    const { error } = await getSupabaseClient().rpc("dismiss_daily_pick", {
      pick: pick.id,
    });
    setBusy(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    queryClient.setQueryData<DailyPicksToday>(QUERY_KEY, (old) =>
      old ? { ...old, picks: old.picks.filter((p) => p.id !== pick.id) } : old,
    );
  };

  const follow = async () => {
    setBusy(true);
    const { data, error } = await getSupabaseClient().rpc("follow_project", {
      project: pick.project_id,
      first_stage: dealStages[0].value,
    });
    setBusy(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    redirect(`/deals/${data}/show`);
  };

  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-start sm:gap-3">
      <div className="flex min-w-0 flex-1 gap-3">
        <span className="w-8 shrink-0 text-lg font-bold leading-6 tabular-nums">
          {pick.score ?? "–"}
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <Link
            to={`/projects/${pick.project_id}/show`}
            className="text-sm font-medium leading-snug line-clamp-2 hover:underline"
          >
            {projectName(pick, locale)}
          </Link>
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <PhaseBadge phase={pick.phase} />
            <span>{countryName(pick.country, locale)}</span>
            <span>{formatBudget(pick, locale)}</span>
            {pick.to_specify && <span>{t("to_specify")}</span>}
          </span>
          <ul
            className="flex flex-wrap gap-1.5"
            data-tour={first ? "reasons" : undefined}
          >
            {pick.reasons.map((reason, index) => (
              <Reason key={index} reason={reason} />
            ))}
          </ul>
        </div>
      </div>
      <div className="flex shrink-0 gap-2 pl-11 sm:pl-0">
        {!invited && (
          <Button size="sm" variant="outline" disabled={busy} onClick={follow}>
            {translate("resources.deals.follow_up.follow")}
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={dismiss}
          title={t("dismiss_help")}
        >
          <X className="size-4" />
          {t("dismiss")}
        </Button>
      </div>
    </li>
  );
};

/** Raison en étiquette : ce qui a changé, sa date, et le lien de la source quand il existe. */
const Reason = ({ reason }: { reason: PickReason }) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const message = reasonMessage(reason);
  if (!message) return null;
  const budget = reason.kind === "budget" ? parseBudget(reason.value) : null;
  const text = translate(
    `resources.projects.daily_picks.reasons.${message.key}`,
    {
      value: budget ? formatBudget(budget, locale) : message.value,
      phase: message.phase
        ? translate(phaseKey(message.phase), { _: message.phase })
        : "",
      date: formatDay(reason.at, locale),
    },
  );
  const body = (
    <>
      <span className="truncate">{text}</span>
      {reason.url && <ExternalLink className="size-3 shrink-0" />}
    </>
  );
  const chip =
    "inline-flex max-w-full items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-0.5 text-xs";
  return (
    <li className="max-w-full">
      {reason.url ? (
        <a
          href={reason.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`${chip} hover:bg-muted`}
          title={translate("resources.projects.daily_picks.source")}
        >
          {body}
        </a>
      ) : (
        <span className={chip}>{body}</span>
      )}
    </li>
  );
};
